import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logActivity } from './useActivityLog';

export type WaitlistReason = 'late_return' | 'returnee' | 'overage';
export type WaitlistResolution = 'rebedded' | 'transferred' | 'left' | 'pending';

export interface DailyWaitlistEntry {
  id: string;
  site_id: string;
  client_id: string | null;
  arrival_name: string;
  arrival_time: string;
  reason: WaitlistReason;
  resolution: WaitlistResolution;
  resolved_at: string | null;
  resolved_by: string | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
}

/** Optionally pass a date (YYYY-MM-DD) to filter to that day; defaults to today. */
export const useDailyWaitlist = (siteId?: string, date?: string) => {
  const qc = useQueryClient();

  useEffect(() => {
    if (!siteId) return;
    const channel = supabase
      .channel(`daily-waitlist-${siteId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'daily_waitlist', filter: `site_id=eq.${siteId}` },
        () => qc.invalidateQueries({ queryKey: ['daily-waitlist', siteId] })
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [siteId, qc]);

  return useQuery({
    queryKey: ['daily-waitlist', siteId, date ?? 'today'],
    enabled: !!siteId,
    queryFn: async () => {
      const day = date ? new Date(date) : new Date();
      const start = new Date(day); start.setHours(0, 0, 0, 0);
      const end = new Date(day); end.setHours(23, 59, 59, 999);

      const { data, error } = await supabase
        .from('daily_waitlist')
        .select('*')
        .eq('site_id', siteId!)
        .gte('arrival_time', start.toISOString())
        .lte('arrival_time', end.toISOString())
        .order('arrival_time', { ascending: false });
      if (error) throw error;
      return (data ?? []) as DailyWaitlistEntry[];
    },
  });
};

export const useAddWaitlistEntry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      site_id: string;
      client_id?: string | null;
      arrival_name: string;
      reason: WaitlistReason;
      notes?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('daily_waitlist')
        .insert({
          site_id: input.site_id,
          client_id: input.client_id ?? null,
          arrival_name: input.arrival_name,
          arrival_time: new Date().toISOString(),
          reason: input.reason,
          resolution: 'pending',
          notes: input.notes ?? null,
          created_by: user.id,
        })
        .select()
        .single();
      if (error) throw error;

      await logActivity({
        actionType: 'bed_count_waitlist_add' as any,
        actionDetails: {
          event: 'bed_count_waitlist_add',
          waitlist_id: data.id,
          arrival_name: input.arrival_name,
          reason: input.reason,
        },
        siteId: input.site_id,
      });

      return data as DailyWaitlistEntry;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['daily-waitlist'] }),
  });
};

export const useResolveWaitlistEntry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      resolution,
      notes,
      siteId,
    }: { id: string; resolution: WaitlistResolution; notes?: string; siteId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('daily_waitlist')
        .update({
          resolution,
          resolved_at: resolution === 'pending' ? null : new Date().toISOString(),
          resolved_by: resolution === 'pending' ? null : user.id,
          notes: notes ?? null,
        })
        .eq('id', id);
      if (error) throw error;

      await logActivity({
        actionType: 'bed_count_waitlist_resolve' as any,
        actionDetails: { event: 'bed_count_waitlist_resolve', waitlist_id: id, resolution },
        siteId,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['daily-waitlist'] }),
  });
};
